import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, MarketplaceSeller } from './catalog.service';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type SellerSortField = 'sellerCode' | 'displayName' | 'status';

@Component({
  standalone: true,
  selector: 'ain-marketplace-sellers',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    CustomerSelectorComponent,
    PartySelectorComponent,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('catalog.marketplace.sellers.title')" [description]="t('catalog.marketplace.sellers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />
      <p class="back"><a routerLink="/marketplace/dashboard">{{ t('catalog.marketplace.dashboard.title') }}</a></p>
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.marketplace.sellers.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="[
                  'catalog.marketplace.help.filters.bullets.search',
                  'catalog.marketplace.help.filters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.searchSellers') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'sellerCode'" (click)="sortBy('sellerCode')">{{ t('catalog.marketplace.sellers.code') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'displayName'" (click)="sortBy('displayName')">{{ t('catalog.marketplace.sellers.name') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'status'" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                  <th>{{ t('catalog.marketplace.sellers.linkedParty') }}</th>
                  <th>{{ t('catalog.marketplace.sellers.linkedCustomer') }}</th>
                  <th>{{ t('catalog.marketplace.sellers.kyc') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.sellerCode }}</button></td>
                  <td>{{ row.displayName }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.partyId || '—' }}</td>
                  <td>{{ row.customerAccountId || '—' }}</td>
                  <td>{{ row.kycReference || '—' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }

      <p-dialog [(visible)]="createVisible" [modal]="true" [style]="{ width: '440px' }">
        <ng-template pTemplate="header">
          <div class="dialog-title">
            <span>{{ t('catalog.marketplace.sellers.create') }}</span>
            <ain-info-hint
              [titleKey]="'catalog.marketplace.help.createSeller.title'"
              [descriptionKey]="'catalog.marketplace.help.createSeller.description'"
              [bulletKeys]="[
                'catalog.marketplace.help.createSeller.bullets.code',
                'catalog.marketplace.help.createSeller.bullets.customer'
              ]" />
          </div>
        </ng-template>
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.sellers.code') }}</span>
            <input pInputText [(ngModel)]="sellerCode" class="full" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.sellers.name') }}</span>
            <input pInputText [(ngModel)]="displayName" class="full" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.sellers.linkedParty') }}</span>
            <ain-party-selector [(ngModel)]="linkedPartyId" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.sellers.linkedCustomer') }}</span>
            <ain-customer-selector [(ngModel)]="linkedCustomerId" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.sellers.kyc') }}</span>
            <input pInputText [(ngModel)]="kycReference" class="full" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitCreate()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .back { margin: 0 0 8px; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .dialog-title { display: flex; align-items: center; gap: 8px; }
    .form { display: grid; gap: 10px; }
    .form label { display: grid; gap: 6px; }
    .full { width: 100%; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class MarketplaceSellersComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveSubscription: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly rows = signal<MarketplaceSeller[]>([]);
  readonly selectedRows = signal<MarketplaceSeller[]>([]);
  readonly info = signal('');
  readonly search = signal('');
  readonly sortField = signal<SellerSortField>('sellerCode');
  readonly sortOrder = signal<1 | -1>(1);

  createVisible = false;
  sellerCode = '';
  displayName = '';
  linkedPartyId: string | null = null;
  linkedCustomerId: string | null = null;
  kycReference = '';
  statusFilter: string | null = null;

  readonly statusOptions = [
    { value: 'Draft', labelKey: 'catalog.marketplace.sellers.status.draft' },
    { value: 'PendingKyc', labelKey: 'catalog.marketplace.sellers.status.pendingKyc' },
    { value: 'Submitted', labelKey: 'catalog.marketplace.sellers.status.submitted' },
    { value: 'UnderReview', labelKey: 'catalog.marketplace.sellers.status.underReview' },
    { value: 'Approved', labelKey: 'catalog.marketplace.sellers.status.approved' },
    { value: 'Suspended', labelKey: 'catalog.marketplace.sellers.status.suspended' },
    { value: 'Rejected', labelKey: 'catalog.marketplace.sellers.status.rejected' }
  ];

  liveStatusKey = liveStatusKey;

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.liveSubscription);
  }

  load(): void {
    this.loading.set(true);
    this.info.set('');
    this.service.listMarketplaceSellers().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredRows(): MarketplaceSeller[] {
    const value = this.search().trim().toLowerCase();
    const status = this.statusFilter;
    const filtered = this.rows().filter(row => {
      if (status && row.status !== status) {
        return false;
      }

      if (!value) {
        return true;
      }

      return row.sellerCode.toLowerCase().includes(value)
        || row.displayName.toLowerCase().includes(value)
        || (row.kycReference ?? '').toLowerCase().includes(value);
    });

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: MarketplaceSeller[] | MarketplaceSeller | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/marketplace/sellers', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'marketplace-sellers.csv',
      ['SellerCode', 'DisplayName', 'Status', 'KycReference'],
      rows.map(row => [row.sellerCode, row.displayName, row.status, row.kycReference ?? '']));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  sortBy(field: SellerSortField): void {
    toggleSortField(this.sortField(), field, this.sortOrder(), v => this.sortField.set(v), v => this.sortOrder.set(v));
  }

  statusKey(status: string): string {
    return `catalog.marketplace.sellers.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  openCreate(): void {
    this.sellerCode = `SELLER-${Date.now().toString().slice(-5)}`;
    this.displayName = '';
    this.linkedPartyId = null;
    this.linkedCustomerId = null;
    this.kycReference = '';
    this.createVisible = true;
  }

  submitCreate(): void {
    if (!this.sellerCode.trim() || !this.displayName.trim()) {
      return;
    }

    this.acting.set(true);
    this.service.createMarketplaceSeller({
      sellerCode: this.sellerCode.trim(),
      displayName: this.displayName.trim(),
      partyId: this.linkedPartyId,
      customerAccountId: this.linkedCustomerId,
      kycReference: this.kycReference.trim() || null
    }).subscribe({
      next: () => {
        this.createVisible = false;
        this.info.set(this.t('catalog.marketplace.sellers.created'));
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  private async startLiveUpdates(): Promise<void> {
    this.liveSubscription = await subscribeMarketplaceOperationsLiveUpdates(
      this.contextService.context()?.tenantId,
      this.liveUpdateService,
      () => this.load());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
